import { useState, useMemo, useCallback, useEffect } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import * as XLSX from 'xlsx'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, Info, Trash2 } from 'lucide-react'
import { ExtractionTable } from '@/components/extraction/ExtractionTable'
import { CitationLink } from '@/components/extraction/CitationLink'
import { RowActionsMenu } from '@/components/extraction/RowActionsMenu'
import { AddItemModal, type ModalFieldConfig } from '@/components/extraction/AddItemModal'
import { BulkActionsBar } from '@/components/ui/bulk-actions-bar'
import { BulkConfirmDelete } from '@/components/extraction/BulkConfirmDelete'
import { useTableFilterBar } from '@/components/extraction/TableFilterBar'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { PackReadinessPill } from '@/components/pack/PackReadinessPill'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatSourceWithPage } from '@/lib/citation-preview'
import { NOTICE_ACT } from '@/lib/notice-tier'
import {
  PACK_PREPARATION_COPY,
  PACK_STATUS_LABELS,
  packReadinessLabel,
} from '@/lib/pack-readiness'
import { formatLodgementDetails } from '@/lib/submission-lodgement'
import { useTableFilters } from '@/hooks/use-table-filters'
import { addItemWithFeedback } from '@/lib/add-item-feedback'
import { buildExportSheet, exportScopeNote } from '@/lib/export-provenance'
import { matchesFilters, type FilterGroups } from '@/lib/table-filters'
import { useExtractionStore } from '@/stores/extraction-store'
import { useSubmissionPackStore } from '@/stores/submission-pack-store'
import type { PackItem, SubmissionStatus, TriageClass } from '@/lib/api'
import type { ExtractedSubmissionItem } from '@/types/extraction'

// ===================
// Types
// ===================

interface SubmissionChecklistExtractionProps {
  bidId: string
  readOnly?: boolean
}

// ===================
// Constants
// ===================

/** Type labels, in the order the Pack groups them. */
const TRIAGE_CLASS_OPTIONS: ReadonlyArray<{ value: TriageClass; label: string }> = [
  { value: 'response', label: 'Response' },
  { value: 'form', label: 'Form' },
  { value: 'attachment', label: 'Attachment' },
  { value: 'acknowledge', label: 'Acknowledgement' },
]

/**
 * Pack status filter options. `not_ready` reads "To do" — the pill splits it
 * further (To do / Missing), but as a filter it is one bucket.
 */
const PACK_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: SubmissionStatus; label: string }> = (
  ['not_ready', 'in_progress', 'ready', 'not_required'] as const
).map((value) => ({ value, label: PACK_STATUS_LABELS[value] }))

// ===================
// Filters
// ===================

/**
 * Type (triage class) and Pack status (effective readiness from the pack).
 *
 * The group keys are deliberately NOT the old `status` / `owner`: a selection
 * persisted under those keys meant the STORED status or an owner, and
 * `pruneValues` drops any group the config no longer names — so an old saved
 * view clears itself instead of silently re-meaning "Pack status".
 */
export function buildSubmissionFilterGroups(
  packItemsById: ReadonlyMap<string, PackItem>
): FilterGroups<ExtractedSubmissionItem> {
  return {
    type: {
      label: 'Type',
      options: TRIAGE_CLASS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      // Same fallback the Pack's grouping uses: an unclassified row is an attachment.
      accessor: (r) => r.triageClass ?? 'attachment',
    },
    readiness: {
      label: 'Pack status',
      options: PACK_STATUS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      // Unknown until the pack loads — such a row matches no pack status.
      accessor: (r) => (r.id ? packItemsById.get(r.id)?.readiness : undefined),
    },
  }
}

// ===================
// Component
// ===================

export function SubmissionChecklistExtraction({
  bidId,
  readOnly,
}: SubmissionChecklistExtractionProps) {
  const navigate = useNavigate()
  const [showAddRow, setShowAddRow] = useState(false)
  const [bulkDelete, setBulkDelete] = useState<{ ids: string[]; aiCount: number } | null>(null)
  const [pendingClear, setPendingClear] = useState<(() => void) | null>(null)

  // Extraction store — the rows ("what the tender asks for")
  const categoryState = useExtractionStore((s) => s.categories.submission_checklist)
  const bulkDeleteItems = useExtractionStore((s) => s.bulkDeleteItems)
  const addManualItem = useExtractionStore((s) => s.addManualItem)
  const deleteItem = useExtractionStore((s) => s.deleteItem)

  // Pack store — the effective readiness. The Pack is the one place progress
  // is tracked; this table only reads it, so the two can never disagree.
  const storePack = useSubmissionPackStore((s) => s.pack)
  const storeBidId = useSubmissionPackStore((s) => s.bidId)
  const refreshPack = useSubmissionPackStore((s) => s.refresh)

  const items = categoryState.items as ExtractedSubmissionItem[]

  useEffect(() => {
    // Fresh state, not the rendered selectors: another mount (the bid header's
    // Needs-from-you badge) may have started this bid's load in the same pass.
    const state = useSubmissionPackStore.getState()
    if (state.bidId === bidId && (state.pack !== null || state.isLoading)) return
    void state.load(bidId)
  }, [bidId])

  // The store is global: a pack left over from another bid is not this one's.
  const pack = storeBidId === bidId ? storePack : null

  const packItemsById = useMemo(() => {
    const map = new Map<string, PackItem>()
    for (const item of pack?.items ?? []) map.set(item.id, item)
    return map
  }, [pack])

  // A row the pack has never seen (a manual add, a re-run's new rows) would sit
  // on "—" until the next visit. Re-read the pack once per new set of unknown
  // ids; keying on the set means an id the pack never returns cannot loop.
  const unknownIdsKey = useMemo(() => {
    if (!pack) return ''
    return items
      .map((i) => i.id)
      .filter((id): id is string => !!id && !packItemsById.has(id))
      .sort()
      .join(',')
  }, [pack, items, packItemsById])

  useEffect(() => {
    if (unknownIdsKey) void refreshPack()
  }, [unknownIdsKey, refreshPack])

  const filterGroups = useMemo(() => buildSubmissionFilterGroups(packItemsById), [packItemsById])

  const filters = useTableFilters<ExtractedSubmissionItem>({
    bidId,
    tableKey: 'submission_checklist',
    groups: filterGroups,
  })

  // Two nodes from one call: the dropdowns go inline in the toolbar, the chip
  // rail goes on its own full-width row beneath it. See `useTableFilterBar`.
  const filterBar = useTableFilterBar({
    groups: filterGroups,
    values: filters.values,
    activeChips: filters.activeChips,
    onToggle: filters.toggle,
    onClear: filters.clear,
    onClearAll: filters.clearAll,
  })

  // Filtered data
  // Hoisted because the hook returns a fresh object every render: depending on
  // `filters` itself would re-run these on every render, while these members
  // are stable (`clearAll`) or change only with the selection.
  const { values: filterValues, clearAll: clearAllFilters } = filters

  // Matched against `filterGroups` directly, not the hook's `matches`: that
  // reads the groups through a ref and is memoised on the selection only, so a
  // pack landing after the first render would leave a Pack status filter
  // matching against the empty pre-load map.
  //
  // Until this bid's pack arrives every row's readiness is unknown, so a saved
  // Pack status filter would hide the whole table behind "No items match".
  // Hold that group back until the pack lands; the cells read "—" meanwhile.
  const appliedFilterValues = useMemo(
    () => (pack ? filterValues : { ...filterValues, readiness: new Set<string>() }),
    [pack, filterValues]
  )
  const matchesRow = useCallback(
    (item: ExtractedSubmissionItem) => matchesFilters(item, filterGroups, appliedFilterValues),
    [filterGroups, appliedFilterValues]
  )

  const filteredItems = useMemo(() => items.filter(matchesRow), [items, matchesRow])

  // Response-class items not yet linked to a document → proposal banner.
  // "Not required" is the Pack's verdict; the stored status stands in only for
  // a row the pack has not answered for yet (not loaded, or not seen).
  const unlinkedResponseCount = useMemo(
    () =>
      items.filter((i) => {
        if (i.triageClass !== 'response' || i.linkedArtifactId) return false
        const packItem = i.id ? packItemsById.get(i.id) : undefined
        return (packItem?.readiness ?? i.status) !== 'not_required'
      }).length,
    [items, packItemsById]
  )

  const handleProposeDocuments = () => {
    // The proposal page resumes an existing unaccepted proposal or starts a new one.
    void navigate(`/app/bids/${bidId}/responses/proposal?start=1`)
  }

  const handleDeleteItem = useCallback(
    async (itemId: string) => {
      await deleteItem(bidId, 'submission_checklist', itemId)
      // The rollup counts the row until the pack is re-read.
      void refreshPack()
    },
    [bidId, deleteItem, refreshPack]
  )

  // Columns — the tender's ask plus the Pack's verdict. Owner, links, notes
  // and status edits live in the Pack only.
  const columns = useMemo<ColumnDef<ExtractedSubmissionItem, unknown>[]>(
    () => [
      {
        id: 'number',
        header: '#',
        size: 40,
        cell: ({ row }) => (
          <span className="text-meta">{row.index + 1}</span>
        ),
      },
      {
        // No size: the Item column takes every pixel the fixed columns leave.
        accessorKey: 'title',
        header: 'Item',
        cell: ({ row }) => (
          <div title={row.original.description || undefined}>
            <span className="text-sm">{row.original.title}</span>
            {row.original.description && (
              <p className="text-meta line-clamp-1 mt-0.5">
                {row.original.description}
              </p>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'triageClass',
        header: 'Type',
        size: 96,
        cell: ({ row }) => {
          const triage = row.original.triageClass
          if (!triage) return <span className="text-meta">—</span>
          // One neutral tag for every type. The accent is reserved for AI and
          // selection, so "Response" must not wear it.
          return (
            <span className="inline-flex rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground capitalize">
              {triage}
            </span>
          )
        },
      },
      {
        id: 'lodgement',
        header: 'How to lodge',
        size: 200,
        cell: ({ row }) => {
          const details = formatLodgementDetails(row.original)
          return details ? (
            <span className="text-meta line-clamp-2" title={details}>
              {details}
            </span>
          ) : (
            <span className="text-meta">—</span>
          )
        },
      },
      {
        id: 'source',
        header: 'Source',
        size: 70,
        cell: ({ row }) => (
          <CitationLink
            page={row.original.sourcePage}
            documentId={row.original.sourceDocumentId ?? null}
            sourceQuote={row.original.sourceQuote}
            sourceHighlightData={row.original.sourceHighlightData ?? null}
          />
        ),
      },
      {
        id: 'packStatus',
        header: 'Pack status',
        // Fits "Not required" with its dot, the widest pill, without wrapping.
        size: 140,
        cell: ({ row }) => {
          // Never the stored status: until the pack answers, say nothing.
          const packItem = row.original.id ? packItemsById.get(row.original.id) : undefined
          if (!packItem) return <span className="text-meta">—</span>
          return (
            <PackReadinessPill
              readiness={packItem.readiness}
              triageClass={packItem.triageClass}
              hasLink={packItem.link !== null}
              statusSource={packItem.statusSource}
              deadline={pack?.deadline ?? null}
            />
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 40,
        cell: ({ row }) => {
          const canDelete =
            !readOnly && row.original.extractionSource === 'manual' && !!row.original.id
          return (
            <RowActionsMenu
              source={row.original.extractionSource ?? 'ai'}
              sourceQuote={row.original.sourceQuote}
              {...(canDelete && {
                onDelete: () => void handleDeleteItem(row.original.id!),
              })}
            />
          )
        },
      },
    ],
    [packItemsById, pack?.deadline, handleDeleteItem, readOnly]
  )

  // Export — the filtered set, matching every other extraction table. The
  // checklist is handed round as a completeness list, so exporting rows the
  // user has filtered out would over-deliver silently.
  //
  // Spreadsheets want columns, so lodgement stays split into Format / Limit /
  // Copies / Method here. Note stays too: it is text the customer typed.
  const handleExport = useCallback(() => {
    const deadline = pack?.deadline ?? null
    const exportData = filteredItems.map((item, i) => {
      const packItem = item.id ? packItemsById.get(item.id) : undefined
      return {
        '#': i + 1,
        Item: item.title,
        Description: item.description || '',
        Type: TRIAGE_CLASS_OPTIONS.find((o) => o.value === item.triageClass)?.label ?? '',
        Format: item.format || '',
        Limit: item.limit || '',
        Copies: item.copies ?? '',
        Method: item.method || '',
        // The Pack's verdict, in the pill's words — never the stored status.
        Status: packItem
          ? packReadinessLabel(
              packItem.readiness,
              packItem.triageClass,
              packItem.link !== null,
              deadline
            ).label
          : '',
        Note: item.note || '',
        Source: item.sourceDocument
          ? formatSourceWithPage(item.sourceDocument, item.sourcePage)
          : '',
      }
    })
    // The checklist is read as a completeness list, so a short sheet is the
    // most dangerous kind here: it renumbers from 1 and reads as "this is
    // everything you have to submit".
    const worksheet = buildExportSheet(
      exportData,
      exportScopeNote({
        chips: filters.activeChips,
        shown: filteredItems.length,
        total: items.length,
      })
    )
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Submission Checklist')
    XLSX.writeFile(
      workbook,
      `submission-checklist-${new Date().toISOString().split('T')[0]}.xlsx`
    )
  }, [filteredItems, packItemsById, pack?.deadline, filters.activeChips, items.length])

  // Add manual item. No status is sent, so the row starts derived and the Pack
  // computes its readiness like any other.
  const handleAddManualItem = useCallback(
    async (values: Record<string, string | boolean>) => {
      const asString = (k: string) => {
        const v = values[k]
        return typeof v === 'string' && v ? v : null
      }
      const copiesRaw = asString('copies')
      const copies = copiesRaw && /^\d+$/.test(copiesRaw) ? parseInt(copiesRaw, 10) : null
      const rawType = asString('triageClass')
      const triageClass =
        TRIAGE_CLASS_OPTIONS.find((o) => o.value === rawType)?.value ?? 'attachment'
      await addItemWithFeedback(
        'Checklist item',
        () =>
          addManualItem(bidId, 'submission_checklist', {
            title: asString('title') ?? '',
            description: asString('description') ?? '',
            triageClass,
            format: asString('format'),
            limit: asString('limit'),
            copies,
            method: asString('method'),
            sourceQuote: '',
            sourceDocument: '',
            sourcePage: 0,
            extractionSource: 'manual',
          }),
        {
          // The new row is unknown to the pack until the re-read lands, so a
          // Pack status filter hides it — which is what the toast should say.
          isVisible: (created) => matchesRow(created as ExtractedSubmissionItem),
          onShowItem: clearAllFilters,
        }
      )
    },
    [bidId, addManualItem, matchesRow, clearAllFilters]
  )

  const modalFields: ModalFieldConfig[] = useMemo(() => [
    { key: 'title', label: 'Item', type: 'text', required: true, fullWidth: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    {
      key: 'triageClass',
      label: 'Type',
      type: 'select',
      options: TRIAGE_CLASS_OPTIONS,
      defaultValue: 'attachment',
    },
    { key: 'format', label: 'Format', type: 'text', placeholder: 'PDF, Word, etc.' },
    { key: 'limit', label: 'Page/word limit', type: 'text' },
    { key: 'copies', label: 'Copies', type: 'text', placeholder: 'e.g. 3' },
    { key: 'method', label: 'Method', type: 'text', placeholder: 'Portal, email, etc.' },
  ], [])

  // Global filter
  const globalFilterFn = useCallback(
    (row: ExtractedSubmissionItem, filterValue: string) => {
      const search = filterValue.toLowerCase()
      return (
        row.title.toLowerCase().includes(search) ||
        row.description.toLowerCase().includes(search) ||
        (row.format?.toLowerCase().includes(search) ?? false) ||
        (row.method?.toLowerCase().includes(search) ?? false)
      )
    },
    []
  )

  const rollup = pack?.rollup ?? null
  const itemNoun = items.length === 1 ? 'item' : 'items'

  return (
    <>
    <div className="mb-3 flex min-w-0 flex-wrap items-center gap-2 rounded-lg bg-muted/40 px-3.5 py-2.5 text-sm">
      <Info className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        This is what the tender asks you to lodge. Progress, owners and linked work are
        tracked in the Submission Pack.
      </span>
      <Link
        to={`/app/bids/${bidId}/submission-pack`}
        className="inline-flex shrink-0 basis-full items-center gap-0.5 whitespace-nowrap sm:basis-auto text-[hsl(var(--link))] hover:underline"
      >
        Open Submission Pack
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </div>
    {unlinkedResponseCount > 0 && (
      // A gap the user has to close, so it keeps the accent — on the edge and
      // the action. The sentence itself is an ordinary count of checklist
      // items; setting it in the accent made it read as a system message.
      <div className={cn(NOTICE_ACT, 'mb-3 flex min-w-0 flex-wrap items-center gap-2 text-sm')}>
        <span className="min-w-0 flex-1">
          <span className="font-medium">
            {unlinkedResponseCount} item{unlinkedResponseCount === 1 ? '' : 's'} need
            {unlinkedResponseCount === 1 ? 's' : ''} a written response
          </span>{' '}
          and {unlinkedResponseCount === 1 ? "isn't" : "aren't"} linked to a response document yet.
        </span>
        <Button size="sm" className="h-auto w-full shrink-0 whitespace-normal py-1.5 sm:h-8 sm:w-auto" onClick={handleProposeDocuments}>
          <GradientSparkleIcon className="h-4 w-4 mr-1.5 shrink-0" />
          Propose response documents
        </Button>
      </div>
    )}
    <ExtractionTable<ExtractedSubmissionItem>
      bidId={bidId}
      category="submission_checklist"
      title="Submission Checklist"
      description="Extract submission requirements, formats, and lodgement methods from tender documents."
      columns={columns}
      data={filteredItems}
      readOnly={readOnly ?? false}
      onExport={handleExport}
      expandableRows
      {...(!readOnly ? { onAddManualItem: () => setShowAddRow(true) } : {})}
      enableSelection={!readOnly}
      renderBulkActions={(rows, clear) => {
        const ids = rows.map((r) => r.id!).filter(Boolean)
        const aiCount = rows.filter((r) => r.extractionSource !== 'manual').length
        return (
          <BulkActionsBar count={rows.length} onClear={clear}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-background hover:bg-destructive hover:text-destructive-foreground gap-1"
              onClick={() => {
                setBulkDelete({ ids, aiCount })
                setPendingClear(() => clear)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </BulkActionsBar>
        )
      }}
      globalFilterFn={globalFilterFn}
      filterContent={filterBar.controls}
      filterChips={filterBar.chips}
      statsBar={(visibleCount) => (
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-meta">
          {/* `visibleCount`, not `filteredItems.length`: the search box lives
              inside ExtractionTable, so only it knows what is on screen. The
              per-status counts below come from the Pack's rollup and
              deliberately count the whole set — a stat that moves when you
              filter by it is a bug. */}
          <span>
            {visibleCount === items.length
              ? `${items.length} ${itemNoun}`
              : `${visibleCount} of ${items.length} ${itemNoun}`}
          </span>
          {rollup && (
            <>
              <span>·</span>
              <span>{PACK_PREPARATION_COPY.preparedCount(rollup.ready)}</span>
              <span>·</span>
              <span>{rollup.inProgress} in progress</span>
              <span>·</span>
              <span>{rollup.notReady} to do</span>
              {rollup.notRequired > 0 && (
                <>
                  <span>·</span>
                  <span>{rollup.notRequired} not required</span>
                </>
              )}
            </>
          )}
        </div>
      )}
    />
    <AddItemModal
      open={showAddRow}
      onOpenChange={setShowAddRow}
      title="Add submission item"
      description="Add a manual submission requirement to track alongside AI-extracted items."
      fields={modalFields}
      onSubmit={handleAddManualItem}
    />
    <BulkConfirmDelete
      open={bulkDelete !== null}
      onOpenChange={(open) => {
        if (!open) {
          setBulkDelete(null)
          setPendingClear(null)
        }
      }}
      count={bulkDelete?.ids.length ?? 0}
      aiCount={bulkDelete?.aiCount ?? 0}
      itemNoun="submission item"
      onConfirm={() => {
        void (async () => {
          if (!bulkDelete) return
          await bulkDeleteItems(bidId, 'submission_checklist', bulkDelete.ids)
          void refreshPack()
          pendingClear?.()
          setBulkDelete(null)
          setPendingClear(null)
        })()
      }}
    />
    </>
  )
}
